import { Fragment, useState, useEffect, useCallback } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { X } from 'lucide-react';
import { calculateCommission } from '../../utils/commissionUtils';
import { toast } from 'react-toastify';
import { getDoc, doc } from 'firebase/firestore';
import { db } from '../../lib/firebase';

interface CancellationRequest {
  orderData: {
    isManual: boolean;
    isPrivateSale?: boolean;
    subtotal: number;
    shippingCost: number;
    total: number;
    source?: string;
    items?: Array<{
      name: string;
      price: number;
      quantity: number;
    }>;
  };
  companyId: string;
  walletTransaction?: {
    amount: number;
    originalAmount: number;
    platformFee: number;
  };
}

interface CancellationApprovalModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (buyerRefund: number, sellerCharge: number) => Promise<void>;
  cancellationRequest: CancellationRequest;
}

export default function CancellationApprovalModal({
  isOpen,
  onClose,
  onConfirm,
  cancellationRequest
}: CancellationApprovalModalProps) {
  const [buyerRefundInput, setBuyerRefundInput] = useState('');
  const [sellerChargeInput, setSellerChargeInput] = useState('');
  const [includeCommission, setIncludeCommission] = useState(false);
  const [loading, setLoading] = useState(false);
  const [commissionInfo, setCommissionInfo] = useState<{
    isExempt: boolean;
    commissionRate: number | null;
    baseCommission: number;
    vatAmount: number;
    totalCommission: number;
  } | null>(null);

  // Garantir que temos os dados antes de acessá-los
  const isManualOrder = cancellationRequest?.orderData?.isManual || false;
  const orderTotal = cancellationRequest?.orderData?.total || 0;
  const orderSubtotal = cancellationRequest?.orderData?.subtotal || 0;
  const orderSource = cancellationRequest?.orderData?.source;
  const isPrivateSale = cancellationRequest?.orderData?.isPrivateSale || false;

  useEffect(() => {
    if (isOpen && cancellationRequest?.orderData) {
      const initializeValues = async () => {
        try {
          const orderData = cancellationRequest.orderData;

          // Para vendas manuais
          if (orderData.isManual) {
            // Se o vendedor está isento ou ainda não temos a informação de comissão
            if (!commissionInfo || commissionInfo.isExempt) {
              setSellerChargeInput('0');
              setIncludeCommission(false);
            } else {
              // Se não está isento, inicializa com o valor da comissão
              setSellerChargeInput(commissionInfo.totalCommission.toString());
              setIncludeCommission(true);
            }
            return; // Sai da função pois já definimos os valores
          }

          // Para outras vendas (não manuais)
          const total = Number(orderData.total) || 0;
          const subtotal = Number(orderData.subtotal) || 0;
          
          setBuyerRefundInput(total.toString());
          
          if (orderData.source === 'marketplace' || orderData.isPrivateSale) {
            setSellerChargeInput(subtotal.toString());
          } else {
            setSellerChargeInput('');
          }
        } catch (error) {
          console.error('Erro ao inicializar valores:', error);
          setBuyerRefundInput('');
          setSellerChargeInput('');
          setIncludeCommission(false);
        }
      };

      initializeValues();
    } else {
      // Limpa todos os valores quando o modal fecha
      setBuyerRefundInput('');
      setSellerChargeInput('');
      setIncludeCommission(false);
    }
  }, [isOpen, cancellationRequest, commissionInfo]);

  useEffect(() => {
    if (cancellationRequest?.orderData?.isManual && commissionInfo) {
      if (commissionInfo.isExempt) {
        setSellerChargeInput('0');
        setIncludeCommission(false);
      }
    }
  }, [commissionInfo, cancellationRequest]);

  useEffect(() => {
    const loadCommissionInfo = async () => {
      if (!isOpen || !cancellationRequest?.orderData || !cancellationRequest.companyId) {
        setCommissionInfo(null);
        return;
      }

      try {
        const userDoc = await getDoc(doc(db, 'users', cancellationRequest.companyId));
        const isExempt = userDoc.exists() && userDoc.data().commissionExempt;

        if (isExempt) {
          setCommissionInfo({
            isExempt: true,
            commissionRate: null,
            baseCommission: 0,
            vatAmount: 0,
            totalCommission: 0
          });
          return;
        }

        const isManualOrder = cancellationRequest.orderData.isManual;
        const commissionRate = isManualOrder ? 0.075 : 0.15;

        const subtotal = Number(cancellationRequest.orderData.subtotal) || 0;
        let baseCommission = Number((subtotal * commissionRate).toFixed(2));
        let vatAmount = Number((baseCommission * 0.23).toFixed(2));
        let totalCommission = Number((baseCommission + vatAmount).toFixed(2));

        if (isManualOrder && totalCommission > 1.23) {
          totalCommission = 1.23;
          baseCommission = Number((1.23 / 1.23).toFixed(2));
          vatAmount = Number((totalCommission - baseCommission).toFixed(2));
        }

        setCommissionInfo({
          isExempt: false,
          commissionRate,
          baseCommission,
          vatAmount,
          totalCommission
        });
      } catch (error) {
        console.error('Erro ao carregar informações de comissão:', error);
        setCommissionInfo(null);
      }
    };

    loadCommissionInfo();
  }, [isOpen, cancellationRequest]);

  const updateSellerCharge = useCallback(() => {
    if (!cancellationRequest?.orderData) return;

    const orderData = cancellationRequest.orderData;
    let baseValue = 0;

    try {
      // Para vendas manuais
      if (orderData.isManual) {
        if (commissionInfo?.isExempt) {
          setSellerChargeInput('0');
          return;
        }
        // Se incluir comissão, usa apenas o valor da comissão
        if (includeCommission && commissionInfo) {
          baseValue = commissionInfo.totalCommission;
        }
      } 
      // Para outras vendas (marketplace e privadas)
      else {
        baseValue = Number(orderData.subtotal) || 0;
        // Adiciona comissão se selecionado e vendedor não isento
        if (includeCommission && commissionInfo && !commissionInfo.isExempt) {
          baseValue += commissionInfo.totalCommission;
        }
      }

      setSellerChargeInput(baseValue.toFixed(2));
    } catch (error) {
      console.error('Erro ao atualizar valor a cobrar:', error);
      setSellerChargeInput('0');
    }
  }, [cancellationRequest, includeCommission, commissionInfo]);

  useEffect(() => {
    updateSellerCharge();
  }, [updateSellerCharge]);

  const handleConfirm = async () => {
    try {
      setLoading(true);
      
      let buyerValue = 0;
      const sellerValue = parseFloat(sellerChargeInput.replace(',', '.'));
      
      if (!isManualOrder) {
        buyerValue = parseFloat(buyerRefundInput.replace(',', '.'));
        if (isNaN(buyerValue)) {
          toast.error('Por favor, insira um valor válido para devolução ao comprador');
          return;
        }
      }

      if (isNaN(sellerValue)) {
        toast.error('Por favor, insira um valor válido para cobrança ao vendedor');
        return;
      }

      await onConfirm(buyerValue, sellerValue);
      onClose();
    } catch (error) {
      console.error('Erro ao confirmar cancelamento:', error);
      toast.error('Erro ao processar o cancelamento. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title
                  as="h3"
                  className="text-lg font-medium leading-6 text-gray-900 flex justify-between items-center"
                >
                  Aprovar Cancelamento
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <X size={24} />
                  </button>
                </Dialog.Title>

                {/* Resumo do Pedido */}
                <div className="mt-4 bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Resumo do Pedido</h4>
                  
                  {/* Detalhes dos Produtos */}
                  {cancellationRequest?.orderData?.items && cancellationRequest.orderData.items.length > 0 && (
                    <div className="space-y-2">
                      {cancellationRequest.orderData.items.map((item, index) => (
                        <div key={index} className="flex justify-between items-center text-sm">
                          <div className="flex items-center space-x-2">
                            <span className="text-gray-600">{item.quantity}x</span>
                            <span className="text-gray-900">{item.name}</span>
                          </div>
                          <span className="text-gray-900">€{(item.price * item.quantity).toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Linha divisória */}
                  <div className="my-3 border-t border-gray-200"></div>

                  {/* Totais */}
                  {cancellationRequest?.orderData && (
                    <div className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Subtotal:</span>
                        <span className="text-gray-900">€{Number(cancellationRequest.orderData.subtotal || 0).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Portes de Envio:</span>
                        <span className="text-gray-900">€{Number(cancellationRequest.orderData.shippingCost || 0).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-sm font-medium">
                        <span className="text-gray-900">Total:</span>
                        <span className="text-gray-900">€{Number(cancellationRequest.orderData.total || 0).toFixed(2)}</span>
                      </div>
                    </div>
                  )}

                  {/* Informações de Comissão */}
                  {cancellationRequest?.orderData && (
                    <div className="mt-3 text-sm">
                      <div className="text-gray-900 font-medium mb-1">Informações de Comissão</div>
                      {commissionInfo ? (
                        commissionInfo.isExempt ? (
                          <div className="text-gray-600">Vendedor isento de comissão</div>
                        ) : (
                          <div className="space-y-1">
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600">Taxa de Comissão:</span>
                              <span className="text-gray-900">{(commissionInfo.commissionRate * 100).toFixed(1)}%</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600">Comissão Base:</span>
                              <span className="text-gray-900">€{commissionInfo.baseCommission.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600">IVA (23%):</span>
                              <span className="text-gray-900">€{commissionInfo.vatAmount.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between text-sm font-medium">
                              <span className="text-gray-900">Comissão Total:</span>
                              <span className="text-gray-900">€{commissionInfo.totalCommission.toFixed(2)}</span>
                            </div>
                            {cancellationRequest.orderData.isManual && commissionInfo.totalCommission === 1.23 && (
                              <div className="text-xs text-gray-500 mt-1">
                                * Comissão limitada a €1,23 (com IVA) para vendas manuais
                              </div>
                            )}
                          </div>
                        )
                      ) : (
                        <div className="text-gray-600">A carregar informações de comissão...</div>
                      )}
                    </div>
                  )}

                  {/* Tipo de Venda */}
                  {cancellationRequest?.orderData && (
                    <div className="mt-3 text-xs text-gray-500">
                      Tipo: {cancellationRequest.orderData.source === 'marketplace' 
                        ? 'Marketplace' 
                        : cancellationRequest.orderData.isPrivateSale 
                          ? 'Venda Privada'
                          : cancellationRequest.orderData.isManual 
                            ? 'Venda Manual'
                            : 'Outro'}
                    </div>
                  )}
                </div>

                <div className="mt-4 space-y-4">
                  {/* Campos de valor apenas se não for venda manual */}
                  {!isManualOrder && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Quanto devolver ao comprador
                      </label>
                      <div className="mt-1 relative rounded-md shadow-sm">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <span className="text-gray-500 sm:text-sm">€</span>
                        </div>
                        <input
                          type="text"
                          value={buyerRefundInput}
                          onChange={(e) => setBuyerRefundInput(e.target.value)}
                          className="block w-full pl-7 pr-12 sm:text-sm border-gray-300 rounded-md"
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                  )}

                  {/* Opções de cobrança para vendas não manuais */}
                  {cancellationRequest?.orderData && !cancellationRequest.orderData.isManual && commissionInfo && !commissionInfo.isExempt && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="space-y-3">
                        <div className="text-sm font-medium text-gray-900">O que descontar ao vendedor?</div>
                        <div className="space-y-2">
                          <label className="flex items-center space-x-2">
                            <input
                              type="radio"
                              name="chargeType"
                              checked={!includeCommission}
                              onChange={() => {
                                setIncludeCommission(false);
                                setSellerChargeInput(cancellationRequest.orderData.subtotal.toString());
                              }}
                              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                            />
                            <span className="text-sm text-gray-900">
                              Apenas produtos (€{Number(cancellationRequest.orderData.subtotal).toFixed(2)})
                            </span>
                          </label>

                          <label className="flex items-center space-x-2">
                            <input
                              type="radio"
                              name="chargeType"
                              checked={includeCommission}
                              onChange={() => {
                                setIncludeCommission(true);
                                const total = Number(cancellationRequest.orderData.subtotal) + commissionInfo.totalCommission;
                                setSellerChargeInput(total.toFixed(2));
                              }}
                              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                            />
                            <span className="text-sm text-gray-900">
                              Produtos + comissão (€{(Number(cancellationRequest.orderData.subtotal) + commissionInfo.totalCommission).toFixed(2)})
                            </span>
                          </label>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Comissão */}
                  {cancellationRequest?.orderData?.isManual && commissionInfo && !commissionInfo.isExempt && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="space-y-3">
                        <div className="text-sm font-medium text-gray-900">O que descontar ao vendedor?</div>
                        <div className="space-y-2">
                          <label className="flex items-center space-x-2">
                            <input
                              type="radio"
                              name="chargeType"
                              checked={!includeCommission && Number(sellerChargeInput) === Number(cancellationRequest.orderData.subtotal)}
                              onChange={() => {
                                setIncludeCommission(false);
                                setSellerChargeInput(cancellationRequest.orderData.subtotal.toString());
                              }}
                              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                            />
                            <span className="text-sm text-gray-900">
                              Apenas produtos (€{Number(cancellationRequest.orderData.subtotal).toFixed(2)})
                            </span>
                          </label>

                          <label className="flex items-center space-x-2">
                            <input
                              type="radio"
                              name="chargeType"
                              checked={includeCommission && Number(sellerChargeInput) === Number(cancellationRequest.orderData.subtotal) + commissionInfo.totalCommission}
                              onChange={() => {
                                setIncludeCommission(true);
                                const total = Number(cancellationRequest.orderData.subtotal) + commissionInfo.totalCommission;
                                setSellerChargeInput(total.toFixed(2));
                              }}
                              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                            />
                            <span className="text-sm text-gray-900">
                              Produtos + comissão (€{(Number(cancellationRequest.orderData.subtotal) + commissionInfo.totalCommission).toFixed(2)})
                            </span>
                          </label>

                          <label className="flex items-center space-x-2">
                            <input
                              type="radio"
                              name="chargeType"
                              checked={includeCommission && Number(sellerChargeInput) === commissionInfo.totalCommission}
                              onChange={() => {
                                setIncludeCommission(true);
                                setSellerChargeInput(commissionInfo.totalCommission.toString());
                              }}
                              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                            />
                            <span className="text-sm text-gray-900">
                              Apenas comissão (€{commissionInfo.totalCommission.toFixed(2)})
                            </span>
                          </label>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Valor a cobrar ao vendedor */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {isManualOrder ? "Total a descontar no vendedor" : "Quanto cobrar ao vendedor"}
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-500 sm:text-sm">€</span>
                      </div>
                      <input
                        type="text"
                        value={sellerChargeInput}
                        onChange={(e) => setSellerChargeInput(e.target.value)}
                        className="block w-full pl-7 pr-12 sm:text-sm border-gray-300 rounded-md"
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={onClose}
                    className="inline-flex justify-center rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-2"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirm}
                    disabled={loading}
                    className={`inline-flex justify-center rounded-md border border-transparent px-4 py-2 text-sm font-medium text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
                      loading
                        ? 'bg-indigo-400 cursor-not-allowed'
                        : 'bg-indigo-600 hover:bg-indigo-700 focus-visible:ring-indigo-500'
                    }`}
                  >
                    {loading ? 'A processar...' : 'Confirmar'}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
